import { useEffect, useRef, useState } from 'react';
import useMap from '../hooks/useMap';
import { BuildingsGeoJsonLayer } from './BuildingsGeoJsonLayer';
import { UserMarkers } from '../components/Markers/UserMarkers';
import { AssetMarkers } from '../components/Markers/AssetMarkers';
import { UnitGeoJsonLayer } from './UnitGeoJsonLayer';
import { useMapStore, useRootStore } from 'contexts/RootStore.context';
import { observer } from 'mobx-react';
import { isNumber } from 'lodash';
import usePolling from '../hooks/usePolling';
import useHandleParams from '../hooks/useHandleParams';
import { useMap as useLeafletMap } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import { DeviceMarkers } from '../components/Markers/DeviceMarkers';


// a generalist controller component for displaying the several layers onto the map.
// also handles the division of markers to the floor level to be passed in if the buildingSelected layer is showing a specific floor.
const LayersController = observer(() => {

  const {
    selectedBuildingId,
    getBuildingLevels,
    outdoorAssets,
    outdoorUsers,
    outdoorDevices,
    showAssetMarkers,
    showUserMarkers,
    showDeviceMarkers,
    getBuildingAssets,
    getBuildingUsers,
    getBuildingDevices,
    selectedFloor,
    selectFloor,
    getFirstNotEmptyFloor,
  } = useMapStore()

  const { panMapToShowAll } = useMap(useMapStore())
  const { mapPollingPeriodSeconds } = useRootStore()
  const { fitMapToBoundsOfGeoJSON } = useMap(useMapStore());
  const postLoadHasRun = useRef(false);
  const map = useLeafletMap(); // note: requires components to be decendant of mapcontainer to invoke this
  const [searchParams] = useSearchParams();
  const [clickThroughToMap, setClickThroughToMap] = useState(searchParams.get("building_id") !== null)

  //select default floor on changing the selected building
  useEffect(() => {
    if (selectedBuildingId === null) {
      selectFloor(null)
    }
    else {
      // do not run when there are url parameters
      if (!clickThroughToMap) {
        selectFloor(getFirstNotEmptyFloor(selectedBuildingId))
        fitMapToBoundsOfGeoJSON(getBuildingLevels(selectedBuildingId))
      }
      else {
        setClickThroughToMap(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId])

  usePolling(mapPollingPeriodSeconds, useMapStore())

  useHandleParams(useMapStore())

  useEffect(() => {
    // post map load functions
    if (!postLoadHasRun.current) {
      panMapToShowAll(map);
      postLoadHasRun.current = true;

      setTimeout(() => {
        // timeout to ensure params aren't removed before needed.
        window.history.replaceState({}, "", window.location.pathname);
      }, 5000);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {selectedBuildingId && isNumber(selectedFloor) && showAssetMarkers && (
        <AssetMarkers assets={getBuildingAssets(selectedBuildingId)[selectedFloor]} />
      )}
      {selectedBuildingId && isNumber(selectedFloor) && showUserMarkers && (
        <UserMarkers users={getBuildingUsers(selectedBuildingId)[selectedFloor]} />
      )}
      {selectedBuildingId && isNumber(selectedFloor) && showDeviceMarkers && (
        <DeviceMarkers devices={getBuildingDevices(selectedBuildingId)[selectedFloor]} />
      )}

      {showAssetMarkers && <AssetMarkers assets={outdoorAssets} />}
      {showUserMarkers && <UserMarkers users={outdoorUsers} />}
      {showDeviceMarkers && <DeviceMarkers devices={outdoorDevices} />}

      {selectedBuildingId && <UnitGeoJsonLayer />}
      <BuildingsGeoJsonLayer />
    </>
  );
})

export default LayersController;
